import React, { useEffect, useMemo, useCallback } from 'react'
import { useSelector, useStore, shallowEqual } from 'react-redux'
import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { StoreProvider, useGenericMappingSelector, useGenericMappingDispatch } from './extra/store'
import Forbidden from '../../../_components/Forbidden'
import { useModal } from '../../../_components/Modal'
import { getMessage } from '../../../common/utils/MessageUtils'
import { useConfirmationModal } from '../../../_components/Modal/useConfirmationModal'
import LoadingMessage from '../../../_components/LoadingMessage'
import { SpinnerButton } from '../../common/CustomButton'
import { getDataTypeDisplayLabel } from '../../dashboardItemMatrix/extra/helpers'
import { dataFixStatus } from '../../../_constants/Code'
import {
  fetchMeta,
  fetchFileTypes,
  fetchMappingConfig,
  fetchValidationResult,
  fetchDataKindStatus,
  fetchBaseFileSet,
  fetchAndParsePreviewCsv,
  saveMapping,
  startValidation,
  fetchPrevMonthMapping
} from './extra/services'
import { useUnsavedChangesModal } from './extra/useUnsavedChangesModal'
import { useMappingConfigModal } from './components/MappingConfigModal'
import PreviewCsvTable from './components/PreviewCsvTable'
import {
  ErrorIcon,
  WarningIcon,
  ValidationMappingLegend,
  getValidationMappingCellTitle
} from './components/ValidationStatusIcons'
import {
  PREVIEW_CHUNK_ROW_COUNT,
  formatMappingItemLabel,
  getProgressDoneCount,
  getProgressPercent,
  getValidationIssueCellKeySet,
  getValidationIssueColumnLevelSet,
  findValidationDetailIndex,
  isValidationCellHighlighted,
  isPositiveRowNumber,
  formatValidationRowNumber,
  filterValidationRows,
  buildInitialMappingInfoFromItem,
  clearPreviewStore,
  createPreviewStore,
  loadPreviewCsvRowsRange,
  savePreviewCsvToStore,
  isNumericColumn,
  showMappingReorderButtons,
  buildDataTypeInfoHeadingLine,
  getDataClassificationDisplayName,
  buildFieldInfoSampleText
} from './extra/helpers'
import { validateSingleFileType } from './extra/validation'
import { FSM, EV, STATUSES_REQUIRING_RESULT_FETCH, deriveSteadyState } from './extra/validationFsm'

const PAGE_TITLE = '汎用データマッピング'
/** 検証詳細一覧 ⇔ マッピング結果CSV の相互ハイライト表示時間（ms） */
const VALIDATION_SYNC_HIGHLIGHT_MS = 3000
const VALIDATION_LOADING_DOT_STYLES = [
  { animationDelay: '0ms' },
  { animationDelay: '150ms' },
  { animationDelay: '300ms' }
]

/** 「検証結果なし」の標準ペイロード。タブ切替や読込失敗時にこの形でストアをクリアする。 リテラルを各所に散らさないことで、フィールド追加時の漏れを防ぐ。 */
const EMPTY_VALIDATION_RESULT = {
  summary: null,
  dataMappingPreviewUrl: null,
  rows: [],
  errorDetailsList: [],
  warningDetailsList: [],
  validationGate: null
}

/**
 * リフレッシュボタンを表示する FSM 状態。
 *
 * 仕様: リフレッシュは「検証実行中の進捗確認」専用機能。VERIFYING および REFRESH_FETCHING でのみ表示する。
 * RESULT_READY（既に結果が確定済み）等では非表示にし、再検証が必要なら「検証実行」ボタンから明示的に始める。
 */
const REFRESHABLE_FSM_STATES = new Set([FSM.VERIFYING, FSM.REFRESH_FETCHING])
/** 検証実行ボタンを活性にする「停止系」FSM 状態（deriveSteadyState の戻り値で判定）。 */
const RUNNABLE_STEADY_STATES = new Set([FSM.READY, FSM.RESULT_READY])
/**
 * 結果表示エリアにページ全体のローダを出す FSM 状態。
 *
 * - INIT: 初回ロード／タブ再入／再保存直後の status 取得中
 * - REFRESH_FETCHING: リフレッシュ押下中。API 応答中は結果表示エリアをローダで覆い、結果が確定したら隠す。
 */
const LOADING_FSM_STATES = new Set([FSM.INIT, FSM.REFRESH_FETCHING])

const sortValidationDetailsByRowAndColumn = (a, b) => {
  if (a.rowNumber !== b.rowNumber) {
    return (Number(a.rowNumber) || 0) - (Number(b.rowNumber) || 0)
  }
  return (a.columnName ?? '').localeCompare(b.columnName ?? '')
}

const mergeValidationDetails = (errors, warnings, errorLabel, warningLabel) => {
  const tagged = [
    ...(errors ?? []).map((e) => ({ ...e, type: errorLabel })),
    ...(warnings ?? []).map((w) => ({ ...w, type: warningLabel }))
  ]
  return tagged.sort(sortValidationDetailsByRowAndColumn)
}

const ValidationErrorDetailsPanel = ({
  errorDetailsList,
  warningDetailsList,
  errorDetailsPanelOpen,
  highlightedErrorIndex,
  onToggle,
  onDownload,
  onSelect
}) => {
  const combinedList = useMemo(
    () => mergeValidationDetails(errorDetailsList, warningDetailsList, 'error', 'warning'),
    [errorDetailsList, warningDetailsList]
  )

  if (combinedList.length === 0) return null
  const count = combinedList.length

  return (
    <div className="box-xy-bordered mb-4 rounded-xl border-blue1 border-2">
      <div className="flex items-center justify-between gap-2 flex-wrap rounded-t-xl bg-[#f1f5f9] px-5 py-3.5">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-h5 font-bold grey--text m-0">⚠️ 検証詳細一覧（{count}件）</h3>
          <span className="text-caption-1 grey--text">
            ※
            エラー・警告は最大1,000件まで表示されます。超過分は表示されないため、表示中のエラーを修正のうえ再度検証を実行してください。
          </span>
        </div>
        <button
          type="button"
          className="blue1--text text-caption-1 font-medium p-0 border-0 bg-transparent cursor-pointer"
          onClick={onToggle}
        >
          {errorDetailsPanelOpen ? '▲ 詳細を非表示' : '▼ 詳細を表示'}
        </button>
      </div>
      {errorDetailsPanelOpen && (
        <>
          {/* 約20行分の高さで固定し、超過分はパネル内の縦スクロールで閲覧する */}
          <div className="overflow-x-auto overflow-y-auto max-h-[720px]">
            <table className="base-table w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="base-table-header bg-[#f1f5f9] font-bold grey--text" title="ファイル名">
                    ファイル名
                  </th>
                  <th className="base-table-header bg-[#f1f5f9] font-bold grey--text" title="行">
                    行
                  </th>
                  <th className="base-table-header bg-[#f1f5f9] font-bold grey--text" title="項目名">
                    項目名
                  </th>
                  <th className="base-table-header bg-[#f1f5f9] font-bold grey--text" title="現在の値">
                    現在の値
                  </th>
                  <th className="base-table-header bg-[#f1f5f9] font-bold grey--text" title="種別">
                    種別
                  </th>
                  <th className="base-table-header bg-[#f1f5f9] font-bold grey--text" title="内容・修正方法">
                    内容・修正方法
                  </th>
                </tr>
              </thead>
              <tbody>
                {combinedList.map((detail, idx) => {
                  const isError = detail.type === 'error'
                  const isHighlighted = highlightedErrorIndex === idx
                  return (
                    <tr
                      key={idx}
                      id={`error-detail-row-${idx}`}
                      className={isHighlighted ? 'bg-blue-50' : 'border-b border-[#e2e8f0]'}
                      role="button"
                      onClick={() => onSelect(detail.rowNumber, detail.columnName)}
                    >
                      <td
                        className="grey--text max-w-[240px] truncate"
                        title={detail.sourceFileName != null ? String(detail.sourceFileName) : ''}
                      >
                        📄 {detail.sourceFileName ?? ''}
                      </td>
                      <td title={formatValidationRowNumber(detail.rowNumber)}>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${
                            isError
                              ? 'border-[#fecaca] bg-[#fef2f2] text-[#b91c1c]'
                              : 'border-[#fde047] bg-[#fffbeb] text-[#a16207]'
                          }`}
                        >
                          {isError ? (
                            <ErrorIcon className="!h-4 !w-4 !text-[11px]" />
                          ) : (
                            <WarningIcon className="!h-4 !w-4 !text-[11px]" />
                          )}
                          {formatValidationRowNumber(detail.rowNumber)}
                        </span>
                      </td>
                      <td className="max-w-[200px] truncate font-bold grey--text" title={detail.columnName ?? ''}>
                        {detail.columnName ?? ''}
                      </td>
                      <td
                        className="grey--text max-w-[200px] truncate"
                        title={detail.currentValue != null ? String(detail.currentValue) : ''}
                      >
                        {detail.currentValue ?? ''}
                      </td>
                      <td
                        title={
                          isError
                            ? `エラー(取込不可) ${detail.errorType != null ? String(detail.errorType) : ''}`
                            : `警告(確認推奨) ${detail.warningType != null ? String(detail.warningType) : ''}`
                        }
                      >
                        <span className="inline-flex items-center gap-1.5 font-medium grey--text">
                          {isError ? <ErrorIcon /> : <WarningIcon />}
                          <span>{isError ? 'エラー(取込不可)' : '警告(確認推奨)'}</span>
                        </span>
                      </td>
                      <td
                        className="align-top"
                        title={[detail.message, detail.suggestion].filter(Boolean).join('\n') || undefined}
                      >
                        <div
                          className={`py-1 grey--text leading-relaxed ${isHighlighted ? 'bg-transparent' : 'bg-white'}`}
                        >
                          {detail.message ?? ''}
                        </div>
                        {detail.suggestion != null && detail.suggestion !== '' && (
                          <div
                            className={`pl-4 py-2 mt-0 grey--text border-l-0 ${isHighlighted ? 'bg-transparent' : 'bg-[#F5FFF7]'}`}
                          >
                            💡 {detail.suggestion}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[#e2e8f0] px-5 py-4">
            <button
              type="button"
              className="btn blue1 lighten-15 blue1--text text-sm font-medium rounded-lg shadow-sm flex items-center gap-2 px-4 py-2.5 border-0"
              onClick={onDownload}
            >
              <svg
                className="w-5 h-5 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>検証結果一覧をCSV出力</span>
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const TAB_BUTTON_BASE_CLASS =
  'flex-1 flex items-center justify-center gap-1.5 py-3.5 px-5 cursor-pointer text-base font-semibold transition-all'
const TAB_STEP_BASE_CLASS =
  'inline-flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 w-7 h-7 transition-all'
const TAB_STEP_ACTIVE_STYLE = {
  background: 'linear-gradient(135deg, #3150A5 0%, #4a6bb5 100%)',
  boxShadow: '0 2px 6px rgba(49,80,165,0.25)'
}

const TabButton = ({ active, stepNumber, label, onClick }) => (
  <button
    type="button"
    className={`${TAB_BUTTON_BASE_CLASS} ${active ? 'bg-white blue1--text' : 'bg-[#f8f9fa] text-[#64748b]'}`}
    style={{
      border: 'none',
      borderBottom: active ? '3px solid #3150a5' : '3px solid transparent'
    }}
    onClick={onClick}
  >
    <span
      className={`${TAB_STEP_BASE_CLASS} ${active ? 'white--text' : 'bg-[#E0E5F2] text-[#64748b]'}`}
      style={active ? TAB_STEP_ACTIVE_STYLE : {}}
    >
      {stepNumber}
    </span>
    <span>{label}</span>
  </button>
)

/**
 * @param {{
 *   urlParams: { dataCreateTaskKey: string; genericKindCode: string }
 *   globalStore: import('redux').Store
 * }} props
 */
const GenericMappingContent = (props) => {
  const { urlParams, globalStore } = props
  const navigate = useNavigate()
  const dispatch = useGenericMappingDispatch()
  const showModal = useModal()
  const openConfirmationModal = useConfirmationModal()

  const pageState = useGenericMappingSelector((s) => s.pageState)
  const currentTab = useGenericMappingSelector((s) => s.currentTab)
  const meta = useGenericMappingSelector((s) => s.meta)
  const mappingData = useGenericMappingSelector((s) => s.mappingData)
  const skippedFields = useGenericMappingSelector((s) => s.skippedFields)
  const hasUnsavedChanges = useGenericMappingSelector((s) => s.hasUnsavedChanges)
  const currentCategory = useGenericMappingSelector((s) => s.currentCategory)
  const validationFsm = useGenericMappingSelector((s) => s.validationFsm, shallowEqual)
  const dataKindStatus = validationFsm.dataKindStatus
  const validationSummary = useGenericMappingSelector((s) => s.validationSummary)
  const validationGate = useGenericMappingSelector((s) => s.validationGate)
  const isBaseFileSet = useGenericMappingSelector((s) => s.isBaseFileSet)
  const dataMappingPreviewUrl = useGenericMappingSelector((s) => s.dataMappingPreviewUrl)
  const previewCsvStatus = useGenericMappingSelector((s) => s.previewCsvStatus)
  const previewCsvRowCount = useGenericMappingSelector((s) => s.previewCsvRowCount ?? 0)
  const validationResultRows = useGenericMappingSelector((s) => s.validationResultRows)
  const currentFilter = useGenericMappingSelector((s) => s.currentFilter)
  const errorDetailsList = useGenericMappingSelector((s) => s.errorDetailsList)
  const warningDetailsList = useGenericMappingSelector((s) => s.warningDetailsList ?? [])
  const genericColumns = useGenericMappingSelector((s) => s.genericColumns)
  const fileTypes = useGenericMappingSelector((s) => s.fileTypes)
  const ruleAppliedFields = useGenericMappingSelector((s) => s.ruleAppliedFields ?? {})
  const lastValidationExecutedAt = useGenericMappingSelector((s) => s.lastValidationExecutedAt)
  const lastValidationExecutedUserName = useGenericMappingSelector((s) => s.lastValidationExecutedUserName)

  const isDataFixed = meta?.dataCreateTaskInfo?.dataFixStatus === dataFixStatus.fix.value
  // 「検証実行中」ラベル表示は dataKindStatus 由来の真値に紐付ける。
  // STARTING は楽観的に dataKindStatus='verifying' をセットするので 1 つの条件に集約できる。
  // リフレッシュ中 (REFRESH_FETCHING) でも下地の dataKindStatus が verifying のままなら
  // ラベルが点滅しないようにするのが目的。
  const isVerifying = validationFsm.dataKindStatus === 'verifying'
  const isValidationRefreshLoading = validationFsm.state === FSM.REFRESH_FETCHING
  const dataKindCode = currentCategory || meta?.genericChrofyColumns?.[0]?.genericTaskCode || ''

  const [pageError, setPageError] = React.useState(null)
  const [serverErrors, setServerErrors] = React.useState([])
  const [validationTabError, setValidationTabError] = React.useState(null)
  const [errorDetailsPanelOpen, setErrorDetailsPanelOpen] = React.useState(true)
  const [highlightedErrorIndex, setHighlightedErrorIndex] = React.useState(null)
  const [highlightedCell, setHighlightedCell] = React.useState(null)
  const highlightedCellClearTimerRef = React.useRef(null)
  const highlightedErrorIndexClearTimerRef = React.useRef(null)
  const previewRequestIdRef = React.useRef(0)
  // 検証関連の非同期処理を latest-wins 判定するための単調増加 ID。
  // CLICK_START / CLICK_REFRESH / TAB_ENTER / KIND_CODE_CHANGE で bump し、
  // 各 async 完了で `isStaleValidationRequest(myId)` を確認、stale なら dispatch しない。
  const validationRequestIdRef = React.useRef(0)
  const currentDataKindCodeRef = React.useRef(urlParams.genericKindCode)
  // 「最新値ミラー」refの描画時更新（fe/core.mdc: 派生値同期のための useEffect は禁止）。
  currentDataKindCodeRef.current = urlParams.genericKindCode

  const bumpValidationRequestId = useCallback(() => ++validationRequestIdRef.current, [])
  const isStaleValidationRequest = useCallback((id) => id !== validationRequestIdRef.current, [])

  const scrollToServerErrors = useCallback(() => {
    document.getElementById('serverErrors')?.scrollIntoView({})
    window.scrollBy(0, -150)
  }, [])

  /** 保存失敗時の共通エラー表示（バナー表示してスクロール） */
  const showSaveError = useCallback(
    (error) => {
      setServerErrors([error?.message ?? getMessage('E011012')])
      setTimeout(scrollToServerErrors, 0)
    },
    [scrollToServerErrors]
  )

  /** レポートデータ更新画面へ戻る */
  const navigateBackToAnalysisData = useCallback(
    () => navigate(`/data/analysisData/${urlParams.dataCreateTaskKey}`),
    [navigate, urlParams.dataCreateTaskKey]
  )

  const scheduleHighlightedCellClear = useCallback(() => {
    if (highlightedCellClearTimerRef.current != null) {
      clearTimeout(highlightedCellClearTimerRef.current)
    }
    highlightedCellClearTimerRef.current = setTimeout(() => {
      highlightedCellClearTimerRef.current = null
      setHighlightedCell(null)
    }, VALIDATION_SYNC_HIGHLIGHT_MS)
  }, [])

  const setHighlightedCellTemporary = useCallback(
    (cell) => {
      setHighlightedCell(cell)
      scheduleHighlightedCellClear()
    },
    [scheduleHighlightedCellClear]
  )

  const scheduleHighlightedErrorIndexClear = useCallback(() => {
    if (highlightedErrorIndexClearTimerRef.current != null) {
      clearTimeout(highlightedErrorIndexClearTimerRef.current)
    }
    highlightedErrorIndexClearTimerRef.current = setTimeout(() => {
      highlightedErrorIndexClearTimerRef.current = null
      setHighlightedErrorIndex(null)
    }, VALIDATION_SYNC_HIGHLIGHT_MS)
  }, [])

  const setHighlightedErrorIndexTemporary = useCallback(
    (idx) => {
      setHighlightedErrorIndex(idx)
      scheduleHighlightedErrorIndexClear()
    },
    [scheduleHighlightedErrorIndexClear]
  )

  useEffect(() => {
    return () => {
      if (highlightedCellClearTimerRef.current != null) {
        clearTimeout(highlightedCellClearTimerRef.current)
      }
      if (highlightedErrorIndexClearTimerRef.current != null) {
        clearTimeout(highlightedErrorIndexClearTimerRef.current)
      }
    }
  }, [])

  const resetPreviewCsv = useCallback(() => {
    previewRequestIdRef.current += 1
    dispatch('RESET_PREVIEW_CSV')
    clearPreviewStore(urlParams.dataCreateTaskKey).catch(() => {})
  }, [dispatch, urlParams.dataCreateTaskKey])

  const loadPreviewCsv = useCallback(
    async (previewUrl) => {
      if (!previewUrl) {
        resetPreviewCsv()
        return
      }

      const requestId = previewRequestIdRef.current + 1
      previewRequestIdRef.current = requestId
      dispatch('RESET_PREVIEW_CSV')
      dispatch('SET_PREVIEW_CSV_STATUS', 'loading')

      try {
        await clearPreviewStore(urlParams.dataCreateTaskKey)
        const parseResult = await fetchAndParsePreviewCsv(previewUrl)
        if (previewRequestIdRef.current !== requestId) return

        const previewStore = createPreviewStore(urlParams.dataCreateTaskKey)
        await savePreviewCsvToStore(previewStore, parseResult.records, parseResult.headers)
        const initialRows = await loadPreviewCsvRowsRange(previewStore, 0, PREVIEW_CHUNK_ROW_COUNT - 1)
        if (previewRequestIdRef.current !== requestId) return

        dispatch('SET_PREVIEW_CSV_DATA', {
          headers: parseResult.headers,
          rows: initialRows,
          rowCount: parseResult.totalRows ?? parseResult.records.length,
          error: null
        })
        dispatch('SET_PREVIEW_CSV_STATUS', 'loaded')
      } catch {
        if (previewRequestIdRef.current !== requestId) return
        dispatch('SET_PREVIEW_CSV_DATA', {
          headers: [],
          rows: [],
          rowCount: 0,
          error: 'マッピング結果CSVの取得に失敗しました。'
        })
        dispatch('SET_PREVIEW_CSV_STATUS', 'error')
      }
    },
    [dispatch, resetPreviewCsv, urlParams.dataCreateTaskKey]
  )

  const applyValidationResultFromResponse = useCallback(
    (r) => {
      const rows = Array.isArray(r.dataMappingPreview) ? r.dataMappingPreview : []
      const dataMappingPreviewUrl = typeof r.dataMappingPreview === 'string' ? r.dataMappingPreview : null
      const errorDetailsList = r.results?.errors ?? []
      const warningDetailsList = r.results?.warnings ?? []
      dispatch('SET_VALIDATION_RESULT', {
        summary: r.summary,
        dataMappingPreviewUrl,
        rows,
        errorDetailsList,
        warningDetailsList,
        // BE が「検証実行可否」を判定して返すゲート（IF02086）。
        // フィールドが無いレスポンス（旧 BE）への後方互換のため、未指定なら null を渡してクリアする。
        validationGate: r.validationGate ?? null
      })
      if (dataMappingPreviewUrl) {
        loadPreviewCsv(dataMappingPreviewUrl).catch(() => {})
      } else {
        resetPreviewCsv()
      }
    },
    [dispatch, loadPreviewCsv, resetPreviewCsv]
  )

  /**
   * 検証結果まわりの表示をすべて空に戻す。 結果取得が失敗した場合や、データ種別を切り替えた場合、検証実行直前に呼び出す。 プレビュー CSV のストアもリセットすることで、旧データの一瞬の表示を防ぐ。
   * 行フィルタも「すべて」に戻し、再検証後に旧フィルタで 0 件表示になるのを防ぐ。
   */
  const clearValidationResult = useCallback(() => {
    resetPreviewCsv()
    dispatch('SET_VALIDATION_RESULT', EMPTY_VALIDATION_RESULT)
    dispatch('SET_CURRENT_FILTER', 'all')
  }, [dispatch, resetPreviewCsv])

  /**
   * 検証タブの初期表示・タブ切替・データ種別切替・再保存後に呼ぶ統合ロード。
   *
   * Sequential 処理に統一:
   *
   * 1. FetchDataKindStatus を必ず先に確定 → `LOAD_STATUS_OK` を FSM に通知
   * 2. ターミナル系（プレビュー対象: verification-error / import-pending / imported / reimport-pending） のときのみ fetchValidationResult
   *    を続けて発行
   *
   * これにより「verifying のままなのに古い結果一覧が見える」race を排除する（仕様 3.2）。 `validationRequestIdRef` で latest-wins 判定し、stale な応答は破棄する。
   */
  const loadValidationResult = useCallback(
    async ({ showError = true } = {}) => {
      if (!dataKindCode) return
      const myId = bumpValidationRequestId()
      const requestDataKindCode = urlParams.genericKindCode
      const isKindStale = () => requestDataKindCode !== currentDataKindCodeRef.current

      setValidationTabError(null)
      dispatch('VALIDATION_EVENT', { type: EV.TAB_ENTER })

      const args = { dataCreateTaskKey: urlParams.dataCreateTaskKey, dataKindCode }
      try {
        const status = await fetchDataKindStatus(globalStore, args)
        if (isKindStale() || isStaleValidationRequest(myId)) return
        dispatch('VALIDATION_EVENT', { type: EV.LOAD_STATUS_OK, payload: { status } })

        if (STATUSES_REQUIRING_RESULT_FETCH.has(status)) {
          const result = await fetchValidationResult(globalStore, args)
          if (isKindStale() || isStaleValidationRequest(myId)) return
          if (result != null) {
            applyValidationResultFromResponse(result)
          } else if (showError) {
            setValidationTabError(getMessage('E10206_007'))
          }
        }
      } catch {
        if (isKindStale() || isStaleValidationRequest(myId)) return
        dispatch('VALIDATION_EVENT', { type: EV.LOAD_FAIL })
        if (showError) setValidationTabError(getMessage('E10206_007'))
      }
    },
    [
      globalStore,
      urlParams.dataCreateTaskKey,
      urlParams.genericKindCode,
      dataKindCode,
      dispatch,
      applyValidationResultFromResponse,
      bumpValidationRequestId,
      isStaleValidationRequest
    ]
  )

  /**
   * 未保存変更モーダルの「破棄」「保存」後に共通で走る後処理。
   *
   * 「タブ切替」「戻る」「その場保留」の 3 ルートを 1 か所にまとめている。 保存後 (`afterSave=true`) は、サーバー側ステータスが変化している可能性があるため、 タブ切替の有無に関わらず
   * `loadValidationResult` を走らせる（ボタン誤活性の防止）。
   *
   * @param {{ kind?: 'tab-switch' | 'navigate-back'; nextTab?: string } | undefined} action
   * @param {{ afterSave: boolean }} flags
   */
  const applyUnsavedFollowUp = useCallback(
    (action, { afterSave }) => {
      // 保存直後はサーバ側で検証結果が無効化される（dataKindStatus が verification-pending 等へ巻き戻る）。
      // ローカルの validationSummary / 前回実行日時 / プレビュー URL / 行データ等は前回値のまま残っており、
      // タブ切替直後に「ロード解除フレーム」で前回結果が一瞬可視化される問題があるため、
      // loadValidationResult を走らせる前に必ず結果ストアと FSM を初期化する。
      // RESAVE_DONE は FSM を INIT / dataKindStatus=null に戻し、isValidationTabLoading=true を維持させる。
      if (afterSave) {
        clearValidationResult()
        dispatch('VALIDATION_EVENT', { type: EV.RESAVE_DONE })
      }
      if (action?.kind === 'tab-switch') {
        dispatch('SET_CURRENT_TAB', action.nextTab)
        const needsLoad = afterSave || action.nextTab === 'validation'
        if (needsLoad) loadValidationResult({ showError: false })
        return
      }
      if (action?.kind === 'navigate-back') {
        navigateBackToAnalysisData()
        return
      }
      // 保存後にタブ切替なし／戻る無しでも、サーバーステータスは変化し得るので再取得する。
      if (afterSave) loadValidationResult({ showError: false })
    },
    [dispatch, navigateBackToAnalysisData, loadValidationResult, clearValidationResult]
  )

  const handleUnsavedDiscard = useCallback(
    (action) => {
      dispatch('RESTORE_SAVED_MAPPING')
      dispatch('SET_IS_UNSAVED_MODAL_OPEN', false)
      applyUnsavedFollowUp(action, { afterSave: false })
    },
    [dispatch, applyUnsavedFollowUp]
  )

  /**
   * マッピング設定の保存処理（バリデーション → 保存 → 保存済み状態の同期）。 未保存モーダルの「保存して戻る」と、フッターの「登録」の共通処理。
   *
   * @returns {Promise<void>} 失敗時は表示用メッセージを持つ Error を throw する
   */
  const executeSave = useCallback(async () => {
    setServerErrors([])
    const singleFileTypeError = validateSingleFileType({
      mappingData: mappingData ?? {},
      genericColumns: genericColumns ?? [],
      dataKindCode,
      dataKindName: getDataClassificationDisplayName(meta, dataKindCode)
    })
    if (singleFileTypeError) throw new Error(singleFileTypeError)

    try {
      await saveMapping(
        globalStore,
        { dataCreateTaskKey: urlParams.dataCreateTaskKey, dataKindCode },
        {
          mappingData: mappingData ?? {},
          skippedFields: skippedFields ?? {},
          genericColumns: genericColumns ?? [],
          ruleAppliedFields: ruleAppliedFields ?? {}
        }
      )
    } catch {
      throw new Error(getMessage('E011012'))
    }

    dispatch('SYNC_SAVED_MAPPING_STATE')
  }, [
    globalStore,
    urlParams.dataCreateTaskKey,
    dataKindCode,
    mappingData,
    skippedFields,
    genericColumns,
    ruleAppliedFields,
    dispatch,
    meta
  ])

  const handleUnsavedSave = useCallback(
    async (action) => {
      await executeSave()
      dispatch('SET_IS_UNSAVED_MODAL_OPEN', false)
      applyUnsavedFollowUp(action, { afterSave: true })
    },
    [executeSave, dispatch, applyUnsavedFollowUp]
  )

  const { openMappingConfigModal } = useMappingConfigModal({
    urlParams,
    globalStore,
    fileTypes: fileTypes ?? [],
    meta,
    onAddMappingItem: (payload) => dispatch('ADD_MAPPING_ITEM', payload)
  })

  const totalCount = useMemo(() => {
    return currentCategory
      ? (genericColumns ?? []).filter((c) => String(c.genericTaskCode) === currentCategory).length
      : (genericColumns ?? []).length
  }, [genericColumns, currentCategory])

  const doneCount = useMemo(
    () =>
      getProgressDoneCount(
        mappingData ?? {},
        skippedFields ?? {},
        genericColumns ?? [],
        currentCategory,
        ruleAppliedFields
      ),
    [mappingData, skippedFields, genericColumns, currentCategory, ruleAppliedFields]
  )

  const progressPercent = getProgressPercent(doneCount, totalCount)
  const unmappedCount = totalCount - doneCount
  const isProgressFull = totalCount > 0 && progressPercent >= 100

  // ローディング表示中 (INIT / REFRESH_FETCHING) はサマリ・空状態プロンプトを隠してチラつきを防ぐ。
  // リフレッシュ押下時もページ全体のローダーを出し、API 応答完了で隠す。
  const isValidationTabLoading = LOADING_FSM_STATES.has(validationFsm.state)
  const isRefreshable = REFRESHABLE_FSM_STATES.has(validationFsm.state)
  const isSummaryVisible = !isValidationTabLoading && validationSummary != null

  // ベースファイル未指定（=アップロードファイルはあるが is_base_file_set が false）の判定（バグ#3160）。
  // IF02095 が期間対応で解決した is_base_file_set を init で取得済み（四半期/年次は対応月次タスクのベースを参照）。
  // ファイルがあるのにベース未指定のときだけ検証を止める（ファイル0件のケースは進捗未充足で別途非活性）。
  const baseFileMissing = !isBaseFileSet && (fileTypes ?? []).length > 0

  // 検証実行ボタンの活性可否。`dataKindStatus` から導出した停止系状態を基準にすることで
  // リフレッシュ押下中 (REFRESH_FETCHING) などの一時遷移ステートに連動して無効化されないようにする。
  // 進捗・データ確定は派生値なのでここで AND するに留める (fe/core.mdc: useEffect 同期禁止)。
  const isValidationRunDisabled = useMemo(
    () =>
      isDataFixed ||
      !isProgressFull ||
      isVerifying ||
      baseFileMissing ||
      validationGate?.canValidate === false ||
      !RUNNABLE_STEADY_STATES.has(deriveSteadyState(validationFsm.dataKindStatus)),
    [isDataFixed, isProgressFull, isVerifying, baseFileMissing, validationGate, validationFsm.dataKindStatus]
  )

  const { open: openUnsavedModal } = useUnsavedChangesModal({
    dispatch,
    currentTab,
    onDiscard: handleUnsavedDiscard,
    onSave: handleUnsavedSave,
    onSaveError: showSaveError
  })

  const validationRowErrorSet = useMemo(() => {
    return getValidationIssueCellKeySet(errorDetailsList ?? [])
  }, [errorDetailsList])

  const validationRowWarningSet = useMemo(() => {
    return getValidationIssueCellKeySet(warningDetailsList ?? [])
  }, [warningDetailsList])

  // 行に紐づかない列単位エラー/警告（区分マスター未設定など）は列全体をハイライトする
  const validationColumnLevelErrorSet = useMemo(() => {
    return getValidationIssueColumnLevelSet(errorDetailsList ?? [])
  }, [errorDetailsList])

  const validationColumnLevelWarningSet = useMemo(() => {
    return getValidationIssueColumnLevelSet(warningDetailsList ?? [])
  }, [warningDetailsList])

  const validationColumnNames = useMemo(() => {
    const rows = validationResultRows ?? []
    const first = rows[0]
    return first && first.columns && typeof first.columns === 'object' ? Object.keys(first.columns) : []
  }, [validationResultRows])

  const filteredValidationRows = useMemo(() => {
    return filterValidationRows(
      validationResultRows ?? [],
      currentFilter,
      errorDetailsList ?? [],
      warningDetailsList ?? []
    )
  }, [validationResultRows, currentFilter, errorDetailsList, warningDetailsList])

  /** マッピング結果テーブル行がある場合、または署名付きURLから取得した原本CSVプレビューが表示されている場合に検証行フィルタを出す */
  const showValidationRowFilter =
    filteredValidationRows.length > 0 ||
    (Boolean(dataMappingPreviewUrl) && previewCsvStatus === 'loaded' && previewCsvRowCount > 0)

  const progressCategoryDisplayName = useMemo(
    () => getDataClassificationDisplayName(meta, currentCategory),
    [meta, currentCategory]
  )

  const hasUploadedFile = (fileTypes ?? []).length > 0
  const hasClassifiedFile = (fileTypes ?? []).some((ft) => (ft?.mappingFileType ?? '').toString().trim() !== '')

  const init = useCallback(async () => {
    dispatch('SET_CURRENT_TAB', 'mapping')
    dispatch('SET_PAGE_STATE', 'LOADING')
    setPageError(null)
    setServerErrors([])
    try {
      const [metaResult, fileTypesResult, mappingConfigResult, isBaseFileSetResult] = await Promise.all([
        fetchMeta(globalStore, urlParams),
        fetchFileTypes(globalStore, urlParams),
        fetchMappingConfig(globalStore, urlParams),
        fetchBaseFileSet(globalStore, urlParams)
      ])
      const mappingColMap = new Map(
        (mappingConfigResult.genericColumns ?? []).map((col) => [`${col.genericTaskCode}_${col.chrofyColumnCode}`, col])
      )
      const genericColumnsWithRule = (metaResult.genericChrofyColumns ?? []).map((c) => {
        const key = `${c.genericTaskCode ?? ''}_${c.chrofyColumnCode ?? ''}`
        const mappingCol = mappingColMap.get(key)
        return {
          genericTaskCode: String(c.genericTaskCode ?? ''),
          chrofyColumnCode: String(c.chrofyColumnCode ?? ''),
          chrofyColumnName: String(c.chrofyColumnName ?? ''),
          hasCustomRule: mappingCol?.hasCustomRule ?? false,
          hasRule: c.hasJudgmentRule === true,
          isRequired: c.isRequired === true,
          chrofyColumnDescription: c.chrofyColumnDescription,
          chrofyColumnDataType: c.chrofyColumnDataType,
          chrofyColumnSample: c.chrofyColumnSample,
          valueMappingColumns: c.valueMappingColumns
        }
      })
      dispatch('INIT', {
        meta: metaResult,
        mappingData: mappingConfigResult.mappingData ?? {},
        skippedFields: mappingConfigResult.skippedFields ?? {},
        ruleAppliedFields: mappingConfigResult.ruleAppliedFields ?? {},
        genericColumns: genericColumnsWithRule,
        fileTypes: fileTypesResult,
        isBaseFileSet: isBaseFileSetResult
      })
      dispatch('SET_PAGE_STATE', 'IDLE')
    } catch {
      setPageError(getMessage('E10206_001'))
      dispatch('SET_PAGE_STATE', 'ERROR')
    }
  }, [urlParams, globalStore, dispatch])

  useEffect(() => {
    init()
    return () => {
      previewRequestIdRef.current += 1
      clearPreviewStore(urlParams.dataCreateTaskKey).catch(() => {})
    }
  }, [])

  // 未保存変更時の離脱防止（beforeunload）。ALLOWED 外のため理由を明記: UX 上ブラウザ離脱前に警告する必要がある。
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  // データ種別を切り替えた直後に、前の種別で実行した検証結果（前回実行日時・検証実行者など）や
  // 「検証実行中」表示が新種別に引き継がれて見えるのを防ぐため、URL の genericKindCode が
  // 変わるたびに検証関連状態を一旦クリアし、検証タブ表示中なら新種別の状態を即時取得し直す。
  // dispatch は useGenericMappingDispatch が毎レンダーで新規関数を返すため依存に含めない（含めると無限ループになる）。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    bumpValidationRequestId()
    dispatch('SET_VALIDATION_RESULT', EMPTY_VALIDATION_RESULT)
    dispatch('VALIDATION_EVENT', { type: EV.KIND_CODE_CHANGE })
    if (currentTab === 'validation') {
      loadValidationResult({ showError: false })
    }
  }, [urlParams.genericKindCode])

  /**
   * 検証実行ハンドラ。
   *
   * 仕様 has-preview-data step 4: 「現在のプレビューを即クリア → 楽観 verifying → API → OK/FAIL」。 楽観セット前に `validationRequestIdRef` を bump
   * して、直前の in-flight リフレッシュ応答が verifying を巻き戻すのを防ぐ。
   */
  const handleValidationRun = useCallback(async () => {
    if (!dataKindCode) return
    const myId = bumpValidationRequestId()
    const prevStatus = validationFsm.dataKindStatus

    clearValidationResult()
    setValidationTabError(null)
    dispatch('VALIDATION_EVENT', { type: EV.CLICK_START })

    try {
      await startValidation(globalStore, {
        dataCreateTaskKey: urlParams.dataCreateTaskKey,
        dataKindCode
      })
      if (isStaleValidationRequest(myId)) return
      dispatch('VALIDATION_EVENT', { type: EV.START_OK })
    } catch {
      if (isStaleValidationRequest(myId)) return
      setValidationTabError(getMessage('E10206_006'))
      dispatch('VALIDATION_EVENT', { type: EV.START_FAIL, payload: { prevStatus } })
    }
  }, [
    globalStore,
    urlParams.dataCreateTaskKey,
    dataKindCode,
    dispatch,
    validationFsm.dataKindStatus,
    clearValidationResult,
    bumpValidationRequestId,
    isStaleValidationRequest
  ])

  /**
   * リフレッシュハンドラ。
   *
   * フロー:
   *
   * 1. VERIFYING 状態でユーザがリフレッシュ押下 → REFRESH_FETCHING へ。
   * 2. `fetchDataKindStatus` を呼び、サーバ最新ステータスを取得。
   * 3. FSM の `REFRESH_STATUS_OK` ハンドラがステータスに応じて遷移先を決める:
   *
   *    - `verifying` → VERIFYING に戻す（結果取得不要、ボタン表示維持）
   *    - ターミナル系（`STATUSES_REQUIRING_RESULT_FETCH` の集合: verification-error / import-pending / imported / reimport-pending） →
   *         REFRESH_FETCHING を維持し、本ハンドラが続けて `fetchValidationResult` を呼ぶ
   *    - それ以外（verification-pending / configuring / null 等）→ VERIFYING に戻す（ローカルロック維持）
   * 4. 失敗時は REFRESH_STATUS_FAIL / REFRESH_RESULT_FAIL を発火し、FSM が VERIFYING に戻して再試行可能にする。
   */
  const handleValidationRefresh = useCallback(async () => {
    if (validationFsm.state === FSM.REFRESH_FETCHING || !dataKindCode) return
    const myId = bumpValidationRequestId()
    const args = { dataCreateTaskKey: urlParams.dataCreateTaskKey, dataKindCode }

    setValidationTabError(null)
    dispatch('VALIDATION_EVENT', { type: EV.CLICK_REFRESH })

    let status
    try {
      status = await fetchDataKindStatus(globalStore, args)
    } catch {
      if (isStaleValidationRequest(myId)) return
      setValidationTabError(getMessage('E10206_007'))
      dispatch('VALIDATION_EVENT', { type: EV.REFRESH_STATUS_FAIL })
      return
    }
    if (isStaleValidationRequest(myId)) return
    dispatch('VALIDATION_EVENT', { type: EV.REFRESH_STATUS_OK, payload: { status } })

    if (!STATUSES_REQUIRING_RESULT_FETCH.has(status)) return

    try {
      const result = await fetchValidationResult(globalStore, args)
      if (isStaleValidationRequest(myId)) return
      applyValidationResultFromResponse(result)
      dispatch('VALIDATION_EVENT', { type: EV.REFRESH_RESULT_OK })
    } catch {
      if (isStaleValidationRequest(myId)) return
      setValidationTabError(getMessage('E10206_007'))
      dispatch('VALIDATION_EVENT', { type: EV.REFRESH_RESULT_FAIL })
    }
  }, [
    validationFsm.state,
    dataKindCode,
    globalStore,
    urlParams.dataCreateTaskKey,
    dispatch,
    applyValidationResultFromResponse,
    bumpValidationRequestId,
    isStaleValidationRequest
  ])

  const handleTabClick = (tab) => {
    if (tab === currentTab) return
    if (hasUnsavedChanges) {
      openUnsavedModal({ kind: 'tab-switch', nextTab: tab })
    } else {
      dispatch('SET_CURRENT_TAB', tab)
      if (tab === 'validation') loadValidationResult({ showError: false })
    }
  }

  /** キャンセル押下。未保存変更があれば確認モーダル、なければレポートデータ更新画面へ戻る。 */
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      openUnsavedModal({ kind: 'navigate-back' })
    } else {
      navigateBackToAnalysisData()
    }
  }, [hasUnsavedChanges, openUnsavedModal, navigateBackToAnalysisData])

  /** 登録押下。確認モーダルで承認後に保存し、画面を再読込する。 */
  const handleRegister = useCallback(() => {
    openConfirmationModal({
      title: '登録の確認',
      description: '変更を登録しますか？',
      confirmName: '登録する',
      isSpinnerDisabled: false, // 保存が非同期のため確認ボタンにスピナーを出す
      onClick: async () => {
        try {
          await executeSave()
        } catch (error) {
          showSaveError(error)
          return
        }
        // 保存後は検証結果を初期化したうえで画面全体を再読込する。
        // init() は await しない：先にモーダルを閉じ、再読込中はページローダーを表示する。
        applyUnsavedFollowUp(undefined, { afterSave: true })
        init()
      }
    })
  }, [openConfirmationModal, executeSave, showSaveError, applyUnsavedFollowUp, init])

  const handleFilterClick = (filter) => {
    dispatch('SET_CURRENT_FILTER', filter)
  }

  const handleApplyRule = (fieldKey, hasExistingMapping) => {
    if (hasExistingMapping) {
      openConfirmationModal({
        title: '確認',
        description: (
          <p>この項目には既にマッピング設定があります。ルール適用にすると設定が削除されます。よろしいですか?</p>
        ),
        confirmName: '適用する',
        onClick: () => dispatch('APPLY_RULE', fieldKey)
      })
    } else {
      dispatch('APPLY_RULE', fieldKey)
    }
  }

  const handleClearRule = (fieldKey) => {
    dispatch('CLEAR_RULE', fieldKey)
  }

  const handleUnskip = (fieldKey) => {
    dispatch('UNSKIP_FIELD', fieldKey)
  }

  const handleSkip = (fieldKey, hasExistingMapping) => {
    if (hasExistingMapping) {
      openConfirmationModal({
        title: '確認',
        description: (
          <p>この項目には既にマッピング設定があります。マッピング不要にすると設定が削除されます。よろしいですか?</p>
        ),
        confirmName: '不要にする',
        onClick: () => dispatch('SKIP_FIELD', fieldKey)
      })
    } else {
      dispatch('SKIP_FIELD', fieldKey)
    }
  }

  const handleShowFieldInfo = useCallback(
    (col) => {
      const sampleText = buildFieldInfoSampleText(col)
      showModal({
        title: '項目情報',
        isCancel: false,
        body: (
          <div>
            <div className="text-center pb-6 mb-8 border-b border-[#e0e5f2]">
              <div className="text-xl font-semibold text-[#3150A5]">{col.chrofyColumnName}</div>
            </div>
            {col.chrofyColumnDescription && (
              <div className="py-5 border-b border-[#f0f0f0]">
                <div className="text-xs font-semibold text-[#666] mb-2.5">説明</div>
                <div className="text-sm text-[#333]">{col.chrofyColumnDescription}</div>
              </div>
            )}
            {col.chrofyColumnDataType && (
              <div className="py-5 border-b border-[#f0f0f0]">
                <div className="text-xs font-semibold text-[#666] mb-2.5">データ型</div>
                <div className="text-sm text-[#333]">{getDataTypeDisplayLabel(col.chrofyColumnDataType)}</div>
              </div>
            )}
            {sampleText && (
              <div className="py-5">
                <div className="text-xs font-semibold text-[#666] mb-2.5">サンプル</div>
                <div className="bg-[#f8f9fa] px-4 py-3.5 rounded-md font-mono text-sm text-[#3150A5] border border-[#e0e5f2]">
                  {sampleText}
                </div>
              </div>
            )}
          </div>
        )
      })
    },
    [showModal]
  )

  const previousTaskCode = meta?.dataCreateTaskInfo?.previousTaskCode ?? ''
  const periodKind = meta?.dataCreateTaskInfo?.periodKind ?? 'monthly'
  const periodLabelMap = { monthly: '月', quarter: '四半期', year: '年' }
  const periodLabel = periodLabelMap[periodKind] ?? '月'
  const copyPrevPeriodTitle = `前${periodLabel}の設定をコピー`

  const handleCopyPrevMonth = useCallback(() => {
    const genericTaskCode = urlParams.genericKindCode || currentCategory || ''
    if (!genericTaskCode || !previousTaskCode) return
    const title = copyPrevPeriodTitle
    showModal({
      title,
      isCancel: false,
      isWide: false,
      description: <p>前{periodLabel}のマッピング設定を現在の期間にコピーしますか？既存の設定は上書きされます。</p>,
      button: [
        { name: 'キャンセル', className: 'footer-btn button_back rounded-[6px]', click: () => {} },
        {
          name: 'コピーする',
          className: 'footer-btn button_base rounded-[6px]',
          click: () => {
            fetchPrevMonthMapping(globalStore, {
              dataCreateTaskKey: urlParams.dataCreateTaskKey,
              genericTaskCode,
              previousTaskCode
            })
              .then(
                ({
                  mappingData: prevMappingData,
                  skippedFields: prevSkippedFields,
                  ruleAppliedFields: prevRuleAppliedFields
                }) => {
                  dispatch('MERGE_PREV_MAPPING', {
                    mappingData: prevMappingData,
                    skippedFields: prevSkippedFields,
                    ruleAppliedFields: prevRuleAppliedFields ?? {}
                  })
                  showModal({
                    title,
                    isCancel: false,
                    isWide: false,
                    description: <p className="green--text">前{periodLabel}のマッピング設定をコピーしました。</p>,
                    button: [{ name: '閉じる', className: 'footer-btn button_base rounded-[6px]', click: () => {} }],
                    footerButtonAreaClass: 'gap-x-8'
                  })
                }
              )
              .catch(() => {
                showModal({
                  title,
                  isCancel: false,
                  isWide: false,
                  description: <p className="red--text">前{periodLabel}のマッピング設定が見つかりませんでした。</p>,
                  button: [{ name: '閉じる', className: 'footer-btn button_base rounded-[6px]', click: () => {} }],
                  footerButtonAreaClass: 'gap-x-8'
                })
              })
          }
        }
      ],
      footerButtonAreaClass: 'gap-x-8'
    })
  }, [urlParams, currentCategory, previousTaskCode, globalStore, dispatch, showModal, copyPrevPeriodTitle, periodLabel])

  const downloadErrorCsv = useCallback(() => {
    const list = mergeValidationDetails(errorDetailsList, warningDetailsList, 'エラー', '警告')
    const header = '種別,ファイル名,行番号,項目名,現在の値,エラー内容,修正方法'
    const escapeCsv = (v) => (v == null ? '' : `"${String(v).replace(/"/g, '""')}"`)
    const rows = list.map((e) =>
      [e.type, e.sourceFileName, e.rowNumber, e.columnName, e.currentValue, e.message, e.suggestion]
        .map(escapeCsv)
        .join(',')
    )
    const blob = new Blob(['\uFEFF' + [header, ...rows].join('\r\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'validation_results.csv'
    a.click()
    URL.revokeObjectURL(url)
  }, [errorDetailsList, warningDetailsList])

  const handleValidationCellClick = useCallback(
    (rowNumber, columnName) => {
      const list = mergeValidationDetails(errorDetailsList, warningDetailsList, 'error', 'warning')
      const idx = findValidationDetailIndex(list, rowNumber, columnName)
      if (idx < 0) return
      if (!isPositiveRowNumber(rowNumber)) {
        setHighlightedCellTemporary({ columnName, columnLevel: true })
      } else {
        setHighlightedCellTemporary({ rowNumber, columnName })
      }
      setErrorDetailsPanelOpen(true)
      setHighlightedErrorIndexTemporary(idx)
      const scrollToDetail = () => {
        const el = document.getElementById(`error-detail-row-${idx}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      scrollToDetail()
      setTimeout(scrollToDetail, 50)
    },
    [errorDetailsList, warningDetailsList, setHighlightedCellTemporary, setHighlightedErrorIndexTemporary]
  )

  const handleErrorDetailRowClick = useCallback(
    (rowNumber, columnName) => {
      if (!isPositiveRowNumber(rowNumber)) {
        setHighlightedCellTemporary({ columnName, columnLevel: true })
      } else {
        setHighlightedCellTemporary({ rowNumber, columnName })
      }
    },
    [setHighlightedCellTemporary]
  )

  if (pageState === 'LOADING') {
    return (
      <main className="page-container">
        <section className="page-content">
          <div className="max-w-[1056px] mx-auto px-6">
            <LoadingMessage message="読み込み中..." minHeightClassName="min-h-[360px]" />
          </div>
        </section>
      </main>
    )
  }

  if (pageState === 'ERROR' && pageError) {
    return (
      <main className="page-container">
        <section className="page-content">
          <div className="max-w-[1056px] mx-auto px-6 p-8">
            <p className="red--text" id="serverErrors">
              {pageError}
            </p>
            <button type="button" className="footer-btn button_back mt-4 rounded-[6px]" onClick={() => init()}>
              再読み込み
            </button>
          </div>
        </section>
      </main>
    )
  }

  const tabLabelMapping = '1 マッピング設定'
  const tabLabelValidation = '2 データ検証'
  const periodText = meta?.dataCreateTaskInfo?.periodValue ?? ''
  const headerDataKindName =
    meta?.genericMappingTask?.genericTaskKindName ?? meta?.genericMappingTask?.genericTaskName ?? ''
  const dataTypeLabel = buildDataTypeInfoHeadingLine(meta)
  const dataKindNameForValidationCopy =
    meta?.genericMappingTask?.genericTaskName ?? meta?.genericMappingTask?.genericTaskKindName ?? ''

  return (
    <div id="wrapper">
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            <header className="page-header flex justify-center py-1 pt-8 mb-2">
              <h1 className="page-header__name text-h1 grey--text py-8">{PAGE_TITLE}</h1>
            </header>

            <div className="sub-title-area flex justify-center items-center py-4 mb-2">
              <h3 className="text-h4 font-bold m-0 leading-none">更新対象データ：</h3>
              {/* 種類名はマスタ登録値で可変長のため、値セルは最小 150px の可変幅とし折り返しを禁止する（table-fixed 不可） */}
              <table className="base-table ml-5 m-0 w-auto align-middle">
                <tbody className="text-h5 text-center">
                  <tr>
                    <td className="blue1 white--text align-middle w-[150px]">種類</td>
                    <td className="border-blue1 align-middle min-w-[150px] whitespace-nowrap">{headerDataKindName}</td>
                  </tr>
                </tbody>
              </table>
              <table className="base-table ml-5 m-0 w-auto table-fixed align-middle">
                <tbody className="text-h5 text-center">
                  <tr>
                    <td className="blue1 white--text align-middle w-[150px]">期間</td>
                    <td className="border-blue1 align-middle w-[150px]">{periodText}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div id="serverErrors" className="mb-4">
              {serverErrors.map((errorMessage, index) => (
                <p key={`${errorMessage}-${index}`} className="red--text">
                  {errorMessage}
                </p>
              ))}
            </div>

            <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-16 px-16 border-blue1 w-full max-w-none">
              <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                {PAGE_TITLE}
              </h2>

              <div className="w-full max-w-none">
                {/* データ種類名と説明エリア（モックアップ dataTypeInfoArea 相当） */}
                <div className="px-8 pt-4 pb-5 border-b border-b-[#E0E5F2] bg-white rounded-t-lg mb-0">
                  <p className="mb-2 font-semibold text-[#666]">{dataTypeLabel}</p>
                  <p className="whitespace-pre text-sm leading-relaxed m-0 text-[#666]">
                    {meta?.genericMappingTask?.genericTaskDescription ?? ''}
                  </p>
                </div>

                {/* タブエリア（外枠線あり・モックアップ tabArea 相当） */}
                <div className="overflow-hidden rounded-b-lg mt-4 p-0 border-none shadow-none">
                  <div className="flex border-b-2 border-b-[#E0E5F2] bg-white rounded-t-lg overflow-hidden">
                    <TabButton
                      active={currentTab === 'mapping'}
                      stepNumber={1}
                      label="マッピング設定"
                      onClick={() => handleTabClick('mapping')}
                    />
                    <TabButton
                      active={currentTab === 'validation'}
                      stepNumber={2}
                      label="データ検証"
                      onClick={() => handleTabClick('validation')}
                    />
                  </div>

                  <div className="px-8 py-5 mt-4 space-y-4 [overflow-anchor:none]">
                    {!hasUploadedFile && (
                      <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded">
                        ファイルをアップロードしてから、マッピング設定を行ってください。
                      </div>
                    )}
                    {hasUploadedFile && !hasClassifiedFile && (
                      <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded">
                        アップロードしたファイルにファイル種別が設定されていません。ファイル種別を設定してから、マッピング設定を行ってください。
                      </div>
                    )}

                    {currentTab === 'mapping' && (
                      <>
                        <h3 className="pb-3 mb-5 font-semibold text-[#333] text-lg border-b-2 border-[#E0E5F2]">
                          項目マッピング設定
                        </h3>
                        <div className="flex justify-between items-center -mt-3 mb-4">
                          <p className="m-0 text-sm grey--text">
                            各システム項目に対して、ファイルのどの項目を取り込むかを設定します。
                          </p>
                          <button
                            type="button"
                            className="shrink-0 m-0 rounded-[6px] border-none px-6 py-[7.5px] text-sm font-medium bg-[#E0E5F2] text-[#3150A5] hover:bg-[#d0d5e2] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                            disabled={!previousTaskCode}
                            onClick={handleCopyPrevMonth}
                          >
                            📋 {copyPrevPeriodTitle}
                          </button>
                        </div>
                        {!isProgressFull && unmappedCount > 0 && hasClassifiedFile && (
                          <div className="mb-4 flex items-center gap-2.5 rounded-md border border-[#ffc107] bg-[#fff3cd] px-4 py-3 text-sm text-[#856404]">
                            <span>⚠️</span>
                            <span>未設定の項目が{unmappedCount}件あります。</span>
                          </div>
                        )}
                        {hasClassifiedFile && (
                          <>
                            <div className="mb-4 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-4">
                              <div className="text-sm font-medium mb-1">
                                📊 マッピング設定の進捗
                                {currentCategory && progressCategoryDisplayName
                                  ? `（${progressCategoryDisplayName}）`
                                  : ''}
                              </div>
                              <div
                                className="w-full h-4 bg-gray-200 rounded overflow-hidden"
                                style={{ '--progress': `${progressPercent}%` }}
                              >
                                <div className="h-full bg-gradient-to-r from-blue-800 to-green-600 progress-bar-fill" />
                              </div>
                              <div className="text-sm mt-1">
                                {doneCount}/{totalCount}件（{progressPercent}%）
                              </div>
                            </div>
                            <div className="overflow-x-auto rounded-lg border border-[#e2e8f0] overflow-hidden">
                              <table className="base-table w-full">
                                <thead>
                                  <tr>
                                    <th className="base-table-header w-[210px] bg-[#f1f5f9] text-[#475569]">
                                      システム項目
                                    </th>
                                    <th className="base-table-header bg-[#f1f5f9] text-[#475569]">マッピング設定</th>
                                    <th className="base-table-header w-[220px] bg-[#f1f5f9] text-[#475569]">操作</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(genericColumns ?? [])
                                    .filter((c) => !currentCategory || String(c.genericTaskCode) === currentCategory)
                                    .map((col) => {
                                      const fieldKey = `${col.genericTaskCode}_${col.chrofyColumnCode}`
                                      const isSkipped = Boolean(skippedFields?.[fieldKey])
                                      const entry = mappingData?.[fieldKey]
                                      const hasMapping = Array.isArray(entry?.items) && entry.items.length > 0
                                      const hasCustomRuleRow = Boolean(col.hasCustomRule || ruleAppliedFields[fieldKey])
                                      const isIdle = !isSkipped && !hasCustomRuleRow
                                      const showReorderButtons = showMappingReorderButtons(entry, col)
                                      return (
                                        <tr key={fieldKey} className={`min-h-[56px] ${isSkipped ? 'bg-gray-100' : ''}`}>
                                          <td className="min-h-[56px] font-bold align-middle py-3">
                                            <span className="inline-flex items-center gap-1.5">
                                              {col.chrofyColumnName}
                                              <button
                                                type="button"
                                                aria-label={`${col.chrofyColumnName}の項目情報`}
                                                className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#3150A5] text-white text-xs font-bold cursor-pointer border-none leading-none shrink-0 hover:opacity-80"
                                                onClick={() => handleShowFieldInfo(col)}
                                              >
                                                i
                                              </button>
                                            </span>
                                          </td>
                                          <td className="min-h-[56px] align-top py-3">
                                            {isSkipped ? (
                                              <span className="inline-block rounded-md border border-dashed border-[#ccc] bg-[#f0f0f0] px-4 py-2 text-sm text-[#888]">
                                                ⭕ マッピング不要
                                              </span>
                                            ) : hasCustomRuleRow ? (
                                              <span
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold"
                                                style={{ background: '#d1fae5', color: '#065f46' }}
                                              >
                                                ⚡ 判定ルールで自動設定
                                              </span>
                                            ) : hasMapping ? (
                                              <div className="text-sm bg-[#fafbfc] border border-[#E0E5F2] rounded-md p-3">
                                                {entry.items.length >= 2 && isNumericColumn(col) && (
                                                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                    <div className="flex border border-[#E0E5F2] rounded overflow-hidden min-w-[200px]">
                                                      <button
                                                        type="button"
                                                        className={`flex-1 px-3.5 py-1.5 border-none text-xs font-medium cursor-pointer text-center transition-colors ${entry.mode === 'priority' ? 'bg-[#3150A5] text-white' : 'bg-white text-[#666] hover:bg-[#f5f7fa]'} border-r border-[#E0E5F2]`}
                                                        onClick={() =>
                                                          dispatch('UPDATE_MAPPING_MODE', {
                                                            fieldKey,
                                                            mode: 'priority'
                                                          })
                                                        }
                                                      >
                                                        優先順位
                                                      </button>
                                                      <button
                                                        type="button"
                                                        className={`flex-1 px-3.5 py-1.5 border-none text-xs font-medium cursor-pointer text-center transition-colors ${entry.mode === 'sum' ? 'bg-[#3150A5] text-white' : 'bg-white text-[#666] hover:bg-[#f5f7fa]'}`}
                                                        onClick={() =>
                                                          dispatch('UPDATE_MAPPING_MODE', { fieldKey, mode: 'sum' })
                                                        }
                                                      >
                                                        合算
                                                      </button>
                                                    </div>
                                                    <span className="grey--text text-xs">
                                                      {entry.mode === 'priority'
                                                        ? '… 上から順に評価し、最初の値を採用'
                                                        : '… すべての値を合計'}
                                                    </span>
                                                  </div>
                                                )}
                                                <div className="flex flex-col gap-1.5">
                                                  {(entry.items ?? []).map((item, idx) => (
                                                    <div
                                                      key={idx}
                                                      className="flex items-center justify-between gap-2 rounded bg-[#f8f9fa] px-3 py-2.5 border-l-[3px] border-l-[#3150A5]"
                                                    >
                                                      <span className="flex-1 text-sm text-[#333]">
                                                        {formatMappingItemLabel(item, {
                                                          conversionTableList: meta?.conversionTableList
                                                        })}
                                                      </span>
                                                      <div className="flex items-center gap-1.5 shrink-0">
                                                        {showReorderButtons && (
                                                          <>
                                                            <button
                                                              type="button"
                                                              className="px-2.5 py-1 text-xs font-medium text-white bg-[#6c757d] rounded cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#5a6268]"
                                                              disabled={idx === 0}
                                                              onClick={() =>
                                                                dispatch('MOVE_MAPPING_ITEM', {
                                                                  fieldKey,
                                                                  fromIndex: idx,
                                                                  toIndex: idx - 1
                                                                })
                                                              }
                                                            >
                                                              ↑
                                                            </button>
                                                            <button
                                                              type="button"
                                                              className="px-2.5 py-1 text-xs font-medium text-white bg-[#6c757d] rounded cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#5a6268]"
                                                              disabled={idx === entry.items.length - 1}
                                                              onClick={() =>
                                                                dispatch('MOVE_MAPPING_ITEM', {
                                                                  fieldKey,
                                                                  fromIndex: idx,
                                                                  toIndex: idx + 1
                                                                })
                                                              }
                                                            >
                                                              ↓
                                                            </button>
                                                          </>
                                                        )}
                                                        <button
                                                          type="button"
                                                          className="px-2.5 py-1 text-xs font-medium text-white bg-[#3150A5] rounded cursor-pointer border-none hover:bg-[#264089]"
                                                          onClick={() => {
                                                            const colMeta = meta?.genericChrofyColumns?.find(
                                                              (c) =>
                                                                `${c.genericTaskCode}_${c.chrofyColumnCode}` ===
                                                                fieldKey
                                                            )
                                                            const initialMappingInfo =
                                                              buildInitialMappingInfoFromItem(item)
                                                            const valueMappingUiType =
                                                              initialMappingInfo?._valueMappingUiType === 'manual'
                                                                ? 'manual'
                                                                : initialMappingInfo?._valueMappingUiType === 'dnd' ||
                                                                    item?.type === 'value_mapping_dnd' ||
                                                                    initialMappingInfo?._isDnd === true
                                                                  ? 'dnd'
                                                                  : null
                                                            const shouldOpenValueMappingDnd =
                                                              (initialMappingInfo?.MappingType ??
                                                                initialMappingInfo?.mappingType) === 'ValueMapping' &&
                                                              valueMappingUiType !== 'manual' &&
                                                              (valueMappingUiType === 'dnd' ||
                                                                (colMeta?.valueMappingColumns?.length ?? 0) > 0)
                                                            openMappingConfigModal({
                                                              fieldKey,
                                                              colName: colMeta?.chrofyColumnName ?? '',
                                                              initialMappingInfo:
                                                                shouldOpenValueMappingDnd && initialMappingInfo
                                                                  ? { ...initialMappingInfo, _isDnd: true }
                                                                  : initialMappingInfo,
                                                              mode: 'edit',
                                                              onSubmitMappingItem: (payload) =>
                                                                dispatch('UPDATE_MAPPING_ITEM', {
                                                                  fieldKey: payload.fieldKey,
                                                                  mappingIndex: idx,
                                                                  item: payload.item
                                                                })
                                                            })
                                                          }}
                                                        >
                                                          編集
                                                        </button>
                                                        <button
                                                          type="button"
                                                          className="px-2.5 py-1 text-xs font-medium text-white bg-[#dc3545] rounded cursor-pointer border-none hover:bg-[#c82333]"
                                                          onClick={() => {
                                                            openConfirmationModal({
                                                              title: '削除確認',
                                                              description: <p>このマッピング設定を削除しますか?</p>,
                                                              confirmName: '削除',
                                                              onClick: () => {
                                                                dispatch('DELETE_MAPPING_ITEM', {
                                                                  fieldKey,
                                                                  mappingIndex: idx
                                                                })
                                                              }
                                                            })
                                                          }}
                                                        >
                                                          削除
                                                        </button>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ) : (
                                              <span className="text-sm grey--text">未設定</span>
                                            )}
                                          </td>
                                          <td className="min-h-[56px] align-middle py-3">
                                            {!isSkipped && !hasCustomRuleRow && (
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <button
                                                  type="button"
                                                  className="px-3 py-1.5 text-xs font-medium text-white bg-[#28a745] rounded cursor-pointer border-none whitespace-nowrap hover:bg-[#218838]"
                                                  onClick={() =>
                                                    openMappingConfigModal({
                                                      fieldKey,
                                                      colName: col.chrofyColumnName
                                                    })
                                                  }
                                                >
                                                  + 追加
                                                </button>
                                                {isIdle && col.hasRule && (
                                                  <button
                                                    type="button"
                                                    className="px-3 py-1.5 text-xs font-medium text-white bg-[#10b981] rounded cursor-pointer border-none whitespace-nowrap hover:bg-[#059669]"
                                                    onClick={() => handleApplyRule(fieldKey, hasMapping)}
                                                  >
                                                    ルール
                                                  </button>
                                                )}
                                                {!col.isRequired && (
                                                  <button
                                                    type="button"
                                                    className="px-3 py-1.5 text-xs font-medium text-white bg-[#6c757d] rounded cursor-pointer border-none whitespace-nowrap hover:bg-[#5a6268]"
                                                    onClick={() => handleSkip(fieldKey, hasMapping)}
                                                  >
                                                    不要
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                            {!isSkipped && hasCustomRuleRow && (
                                              <button
                                                type="button"
                                                className="px-3 py-1.5 text-xs font-medium text-[#333] bg-[#ffc107] rounded cursor-pointer border-none whitespace-nowrap hover:bg-[#e0a800]"
                                                onClick={() => handleClearRule(fieldKey)}
                                              >
                                                ルール適用を解除
                                              </button>
                                            )}
                                            {isSkipped && (
                                              <button
                                                type="button"
                                                className="px-3 py-1.5 text-xs font-medium text-[#333] bg-[#ffc107] rounded cursor-pointer border-none whitespace-nowrap hover:bg-[#e0a800]"
                                                onClick={() => handleUnskip(fieldKey)}
                                              >
                                                不要を解除
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      )
                                    })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {currentTab === 'validation' && (
                      <>
                        <div className="pb-3 mb-5 font-semibold text-[#333] text-lg border-b-2 border-[#E0E5F2]">
                          検証を実行
                        </div>
                        <p className="text-sm text-[#666] -mt-3 mb-4">
                          「{dataKindNameForValidationCopy || currentCategory || '対象'}
                          」のマッピング設定が正しく動作するか検証します。
                        </p>
                        {!isProgressFull && unmappedCount > 0 && hasClassifiedFile && (
                          <div className="mb-4 flex items-center gap-2.5 rounded-md border border-[#ffc107] bg-[#fff3cd] px-4 py-3 text-sm text-[#856404]">
                            <span>⚠️</span>
                            <span>未設定の項目が{unmappedCount}件あります。</span>
                          </div>
                        )}
                        {validationTabError && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                            {validationTabError}
                          </div>
                        )}
                        <div className="mb-4 flex flex-col gap-4 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-5">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              className="blue1 white--text inline-flex items-center justify-center rounded-[6px] border-none px-5 py-2 text-sm font-medium leading-none transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isValidationRunDisabled}
                              onClick={handleValidationRun}
                            >
                              {isVerifying ? (
                                <span
                                  aria-label="⏳ 検証実行中..."
                                  className="inline-flex items-center justify-center gap-1"
                                >
                                  <span aria-hidden="true">⏳</span>
                                  <span className="font-bold">検証実行中</span>
                                  <span aria-hidden="true" className="inline-flex">
                                    {VALIDATION_LOADING_DOT_STYLES.map((style, index) => (
                                      <span
                                        key={`validation-loading-dot-${index}`}
                                        className="inline-block animate-pulse"
                                        style={style}
                                      >
                                        .
                                      </span>
                                    ))}
                                  </span>
                                </span>
                              ) : (
                                <span>🔍 検証実行</span>
                              )}
                            </button>
                            {isRefreshable && (
                              <button
                                type="button"
                                className="green2 white--text ml-auto inline-flex items-center justify-center gap-1.5 rounded-[6px] border-none px-5 py-2 text-sm font-medium leading-none transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isValidationRefreshLoading}
                                onClick={handleValidationRefresh}
                              >
                                <span
                                  aria-hidden="true"
                                  className={`inline-flex items-center justify-center ${
                                    isValidationRefreshLoading ? 'animate-spin' : ''
                                  }`}
                                >
                                  ↻
                                </span>
                                <span>{isValidationRefreshLoading ? '結果を更新中...' : '結果を更新'}</span>
                              </button>
                            )}
                          </div>
                          {validationGate?.canValidate === false &&
                            validationGate?.reason === 'data_create_in_progress' && (
                              <div className="text-caption-2 red--text">{getMessage('E10206_008')}</div>
                            )}
                          {baseFileMissing && (
                            <div className="text-caption-2 red--text">{getMessage('E10206_009')}</div>
                          )}
                          {(lastValidationExecutedAt != null ||
                            (lastValidationExecutedUserName ?? '').trim() !== '') && (
                            <div className="flex flex-col gap-1 border-t border-[#e2e8f0] pt-4">
                              {lastValidationExecutedAt != null && (
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="min-w-[100px] font-medium text-[#64748b]">前回実行日時:</span>
                                  <span>{lastValidationExecutedAt}</span>
                                </div>
                              )}
                              {(lastValidationExecutedUserName ?? '').trim() !== '' && (
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="min-w-[100px] font-medium text-[#64748b]">検証実行者:</span>
                                  <span>{lastValidationExecutedUserName}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {isValidationTabLoading && (
                          <div className="mt-8">
                            <LoadingMessage message="読み込み中..." minHeightClassName="min-h-[240px]" />
                          </div>
                        )}
                        {!isValidationTabLoading && !isSummaryVisible && (
                          <div className="mt-8 text-center py-16 px-5 bg-[#f8fafc] border-2 border-dashed border-[#e2e8f0] rounded-lg text-[#64748b]">
                            <div className="text-4xl mb-3">📋</div>
                            <div className="text-[15px]">検証を実行すると、ここに結果が表示されます</div>
                          </div>
                        )}
                        {isSummaryVisible && (
                          <>
                            <div className="pb-3 mb-5 font-semibold text-[#333] text-lg border-b-2 border-[#E0E5F2] mt-8">
                              検証結果
                            </div>
                            <div className="mb-4">
                              <div className="flex gap-3 text-sm flex-wrap">
                                <div
                                  className="flex flex-1 flex-col gap-1 rounded-md border-l-[3px] border-l-[#3b82f6] bg-[#eff6ff] px-3 py-2"
                                  title={`対象 ${(validationSummary.totalRecords ?? 0).toLocaleString()}行`}
                                >
                                  <span className="text-xs text-[#64748b]">対象</span>
                                  <span className="font-bold">
                                    {(validationSummary.totalRecords ?? 0).toLocaleString()}行
                                  </span>
                                </div>
                                <div
                                  className="flex flex-1 flex-col gap-1 rounded-md border-l-[3px] border-l-[#22c55e] bg-[#f0fdf4] px-3 py-2"
                                  title={`正常 ${(validationSummary.successCount ?? 0).toLocaleString()}行`}
                                >
                                  <span className="text-xs text-[#64748b]">正常</span>
                                  <span className="font-bold green--text">
                                    {(validationSummary.successCount ?? 0).toLocaleString()}行
                                  </span>
                                </div>
                                <div
                                  className="flex flex-1 flex-col gap-1 rounded-md border-l-[3px] border-l-[#ef4444] bg-[#fef2f2] px-3 py-2"
                                  title={`エラー(取込不可) ${(validationSummary.errorCount ?? 0).toLocaleString()}行`}
                                >
                                  <span className="inline-flex items-center gap-1.5 text-xs text-[#64748b]">
                                    <ErrorIcon className="!h-3.5 !w-3.5 !text-[10px]" />
                                    エラー
                                  </span>
                                  <span className="font-bold red--text">
                                    {(validationSummary.errorCount ?? 0).toLocaleString()}行
                                  </span>
                                </div>
                                <div
                                  className="flex flex-1 flex-col gap-1 rounded-md border-l-[3px] border-l-[#f59e0b] bg-[#fffbeb] px-3 py-2"
                                  title={`警告(確認推奨) ${(validationSummary.warningCount ?? 0).toLocaleString()}行`}
                                >
                                  <span className="inline-flex items-center gap-1.5 text-xs text-[#64748b]">
                                    <WarningIcon className="!h-3.5 !w-3.5 !text-[10px]" />
                                    警告
                                  </span>
                                  <span className="font-bold yellow--text">
                                    {(validationSummary.warningCount ?? 0).toLocaleString()}行
                                  </span>
                                </div>
                              </div>
                              {validationSummary.errorCount === 0 && (
                                <div className="mt-3 flex items-center gap-3 rounded-lg border-l-4 border-l-[#22c55e] px-5 py-4 bg-gradient-to-r from-[#f0fdf4] to-[#dcfce7]">
                                  <span className="text-xl">✅</span>
                                  <span className="text-sm font-medium text-[#166534]">
                                    このカテゴリの検証は正常に完了しました。エラーはありません。
                                  </span>
                                </div>
                              )}
                            </div>
                            {showValidationRowFilter && (
                              <div className="mb-4 flex items-center gap-3 bg-[#f8fafc] px-4 py-3 rounded-lg">
                                <span className="text-sm text-[#64748b]">表示:</span>
                                <div className="flex border border-[#e2e8f0] rounded-md overflow-hidden">
                                  <button
                                    type="button"
                                    className={`px-3 py-2 border-none text-xs cursor-pointer ${currentFilter === 'all' ? 'bg-[#3150A5] text-white' : 'bg-white'}`}
                                    onClick={() => handleFilterClick('all')}
                                  >
                                    すべて
                                  </button>
                                  <button
                                    type="button"
                                    className={`px-3 py-2 border-none border-l border-l-[#e2e8f0] text-xs cursor-pointer ${currentFilter === 'error' ? 'bg-[#3150A5] text-white' : 'bg-white'}`}
                                    onClick={() => handleFilterClick('error')}
                                  >
                                    エラーのみ
                                  </button>
                                  <button
                                    type="button"
                                    className={`px-3 py-2 border-none border-l border-l-[#e2e8f0] text-xs cursor-pointer ${currentFilter === 'warning' ? 'bg-[#3150A5] text-white' : 'bg-white'}`}
                                    onClick={() => handleFilterClick('warning')}
                                  >
                                    警告のみ
                                  </button>
                                </div>
                              </div>
                            )}
                            {dataMappingPreviewUrl && (
                              <PreviewCsvTable
                                taskKey={urlParams.dataCreateTaskKey}
                                highlightedCell={highlightedCell}
                                onErrorCellClick={handleValidationCellClick}
                              />
                            )}
                            {filteredValidationRows.length > 0 && (
                              <div className="mb-2 rounded-lg border border-[#e2e8f0] overflow-hidden">
                                <h3 className="font-medium mb-2 px-4 pt-3">マッピング結果</h3>
                                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                                  <table className="base-table w-full">
                                    <thead>
                                      <tr>
                                        <th className="base-table-header text-left" title="行番号">
                                          行番号
                                        </th>
                                        <th className="base-table-header text-left" title="従業員ID">
                                          従業員ID
                                        </th>
                                        {validationColumnNames.map((colName) => (
                                          <th key={colName} className="base-table-header text-left" title={colName}>
                                            {colName}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredValidationRows.map((row, i) => (
                                        <tr key={i}>
                                          <td
                                            className="grey--text font-mono text-sm"
                                            title={row.rowNumber != null ? String(row.rowNumber) : ''}
                                          >
                                            {row.rowNumber}
                                          </td>
                                          <td title={row.employeeId != null ? String(row.employeeId) : ''}>
                                            {row.employeeId ?? ''}
                                          </td>
                                          {validationColumnNames.map((colName) => {
                                            const val = row.columns?.[colName]
                                            const cellKey = `${row.rowNumber}-${colName}`
                                            const isError =
                                              validationRowErrorSet.has(cellKey) ||
                                              validationColumnLevelErrorSet.has(colName)
                                            const isWarning =
                                              !isError &&
                                              (validationRowWarningSet.has(cellKey) ||
                                                validationColumnLevelWarningSet.has(colName))
                                            const isHighlighted = isValidationCellHighlighted(
                                              highlightedCell,
                                              row.rowNumber,
                                              colName
                                            )
                                            const isEmpty = val === undefined || val === null || val === ''
                                            const cellTitle = getValidationMappingCellTitle({
                                              isError,
                                              isWarning,
                                              isEmpty,
                                              rawValue: val
                                            })
                                            return (
                                              <td
                                                key={colName}
                                                className={
                                                  isHighlighted
                                                    ? 'bg-blue-100'
                                                    : isError
                                                      ? 'cursor-pointer bg-red-50 text-red-900'
                                                      : isWarning
                                                        ? 'bg-amber-50 text-amber-900'
                                                        : isEmpty
                                                          ? 'italic text-[#94a3b8]'
                                                          : ''
                                                }
                                                title={cellTitle}
                                                role={isError || isWarning ? 'button' : undefined}
                                                onClick={() =>
                                                  (isError || isWarning) &&
                                                  handleValidationCellClick(row.rowNumber, colName)
                                                }
                                              >
                                                <div className="flex min-w-0 items-center gap-1.5 px-0.5 py-0.5">
                                                  {isError && <ErrorIcon />}
                                                  {!isError && isWarning && <WarningIcon />}
                                                  <span className="min-w-0 truncate">
                                                    {isEmpty ? '(未入力) 空値' : String(val)}
                                                  </span>
                                                </div>
                                              </td>
                                            )
                                          })}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <ValidationMappingLegend />
                              </div>
                            )}
                            <ValidationErrorDetailsPanel
                              errorDetailsList={errorDetailsList}
                              warningDetailsList={warningDetailsList}
                              errorDetailsPanelOpen={errorDetailsPanelOpen}
                              highlightedErrorIndex={highlightedErrorIndex}
                              onToggle={() => setErrorDetailsPanelOpen(!errorDetailsPanelOpen)}
                              onDownload={downloadErrorCsv}
                              onSelect={handleErrorDetailRowClick}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>

                  <div className="content-footer mt-16 mb-8">
                    <div className="border-t border-blue1" />
                    {/*  ===== フッターボタン ===== */}
                    <div className="footer_button_area mt-8">
                      <button type="button" className="footer-btn button_back" onClick={handleCancel}>
                        キャンセル
                      </button>
                      {/* データ検証タブは保存対象を持たないため登録ボタンを出さない。 */}
                      {currentTab === 'mapping' && (
                        <SpinnerButton name="登録" className="footer-btn button_base" onClick={handleRegister} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

/**
 * 権限: セッション有効で data_create_task_key / generic_mapping_task_key へのアクセス権があること。
 *
 * @returns {React.ReactElement}
 */
const GenericMapping = () => {
  const { dataCreateTaskKey, key: genericKindCode } = useParams()
  const state = useSelector(
    (s) => ({
      login: s.login,
      user: s.user,
      authority: s.authority
    }),
    shallowEqual
  )

  if (!state.login || !state.user) {
    return <Navigate to="/login" replace />
  }

  const hasDataAccess =
    state.authority?.Data_AuthInfo?.Availability !== false &&
    state.authority?.AnalysisData_AuthInfo?.Availability !== false
  if (state.authority && !hasDataAccess) {
    return <Forbidden />
  }

  // アップロード可能データ種別の権限チェック
  // - `genericKindCode` は `generic kind code` と同義
  // - `Uploadable_DataKind.Files` に含まれる場合のみアクセス許可
  const uploadableConf = state.authority?.Upload_AuthInfo?.Auth_Conf?.Uploadable_DataKind
  const uploadableDataKindFiles = uploadableConf?.Files ?? []
  const hasUploadablePermission =
    uploadableConf?.Availability !== false && uploadableDataKindFiles.includes(String(genericKindCode ?? ''))
  if (state.authority && !hasUploadablePermission) {
    return <Forbidden />
  }

  const urlParams = {
    dataCreateTaskKey: dataCreateTaskKey ?? '',
    genericKindCode: genericKindCode ?? ''
  }
  const globalStore = useStore()

  return (
    <StoreProvider>
      <GenericMappingContent urlParams={urlParams} globalStore={globalStore} />
    </StoreProvider>
  )
}

export default GenericMapping
export { GenericMappingContent }
